import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, GripVertical, Loader2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import { useReorderProductVariants } from '@/hooks/useProduct';
import { NurseryPriceEditor } from './NurseryPriceEditor';
import { NurseryOnlyToggle } from './NurseryOnlyToggle';
import { VariantFieldCell } from './VariantFieldCell';
import { VariantInventoryCell } from './VariantInventoryCell';

/**
 * The minimal variant shape this table edits. Both `VariantRow` (from the
 * variant list view) and a mapped `Variant` (from the product detail fetch)
 * satisfy it, so the table is shared by the Products list's expandable rows and
 * the product detail page.
 */
export interface EditableVariant {
  id: string;
  shopify_variant_id: number;
  shopify_product_id: number;
  shopify_inventory_item_id: number;
  sku: string | null;
  online_price: number | null;
  nursery_price: number | null;
  nursery_only: boolean | null;
  inventory_quantity: number | null;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  variant_title: string | null;
  position: number | null;
}

function variantsInPositionOrder(variants: EditableVariant[]): EditableVariant[] {
  return [...variants].sort(
    (a, b) =>
      (a.position ?? Number.MAX_SAFE_INTEGER) -
        (b.position ?? Number.MAX_SAFE_INTEGER) ||
      a.shopify_variant_id - b.shopify_variant_id,
  );
}

/**
 * An editable table of a product's variants — SKU, online price, nursery price,
 * stock and the nursery-only flag, with the Size option editable inline. On the
 * product detail page, rows can also be reordered. Changes write through to
 * Shopify.
 */
export function VariantEditTable({
  variants,
  reorderable = false,
}: {
  variants: EditableVariant[];
  reorderable?: boolean;
}) {
  const persistedOrder = variantsInPositionOrder(variants).map(
    (variant) => variant.shopify_variant_id,
  );
  const persistedOrderKey = persistedOrder.join(',');
  const [orderedVariantIds, setOrderedVariantIds] = useState(persistedOrder);
  const [draggedVariantId, setDraggedVariantId] = useState<number | null>(null);
  const reorder = useReorderProductVariants();

  useEffect(() => {
    setOrderedVariantIds(persistedOrderKey === '' ? [] : persistedOrderKey.split(',').map(Number));
  }, [persistedOrderKey]);

  if (variants.length === 0) {
    return <p className="px-4 py-4 text-xs text-muted-foreground">No variants.</p>;
  }

  const variantsById = new Map(
    variants.map((variant) => [variant.shopify_variant_id, variant]),
  );
  const orderedVariants = orderedVariantIds
    .map((id) => variantsById.get(id))
    .filter((variant): variant is EditableVariant => variant != null);
  const canReorder = reorderable && orderedVariants.length > 1;

  const saveOrder = (next: EditableVariant[]) => {
    if (!canReorder || reorder.isPending) return;

    const previousIds = orderedVariantIds;
    const nextIds = next.map((variant) => variant.shopify_variant_id);
    setOrderedVariantIds(nextIds);
    reorder.mutate(
      {
        shopifyProductId: next[0].shopify_product_id,
        shopifyVariantIds: nextIds,
      },
      {
        onSuccess: () => toast.success('Variant order saved to Shopify'),
        onError: (err) => {
          setOrderedVariantIds(previousIds);
          toast.error(`Saved locally, but Shopify update failed: ${err.message}`);
        },
      },
    );
  };

  const moveVariant = (fromIndex: number, toIndex: number) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= orderedVariants.length ||
      toIndex >= orderedVariants.length
    ) {
      return;
    }
    const next = [...orderedVariants];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    saveOrder(next);
  };

  const dropVariantAt = (toIndex: number) => {
    if (draggedVariantId == null) return;
    const fromIndex = orderedVariants.findIndex(
      (variant) => variant.shopify_variant_id === draggedVariantId,
    );
    setDraggedVariantId(null);
    moveVariant(fromIndex, toIndex);
  };

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-background">
      {/* table-fixed: column widths come from the <th> widths below, never from
          cell content. Without it, a cell turning into an <input> on edit makes
          the browser re-measure the column and the whole table shifts. */}
      <table className="w-full min-w-[680px] table-fixed text-sm">
        <thead>
          <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="w-[20%] px-2.5 py-1.5 text-left font-medium">
              Variant (size)
            </th>
            <th className="w-[18%] px-2.5 py-1.5 text-left font-medium">SKU</th>
            <th className="w-[16%] px-2.5 py-1.5 text-left font-medium">Online price</th>
            <th className="w-[16%] px-2.5 py-1.5 text-left font-medium">Nursery price</th>
            <th className="w-[13%] px-2.5 py-1.5 text-left font-medium">Stock</th>
            <th className="w-[17%] px-2.5 py-1.5 text-left font-medium">Nursery only</th>
          </tr>
        </thead>
        <tbody>
          {orderedVariants.map((v, index) => (
            <tr
              key={v.id}
              onDragOver={(event) => {
                if (canReorder && draggedVariantId != null) event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                dropVariantAt(index);
              }}
              className={`border-b border-border last:border-0 ${
                draggedVariantId === v.shopify_variant_id ? 'opacity-50' : ''
              }`}
            >
              <td className="px-2.5 py-1">
                <div className="flex min-w-0 items-center gap-1">
                  {canReorder && (
                    <div className="flex shrink-0 items-center">
                      <button
                        type="button"
                        draggable={!reorder.isPending}
                        disabled={reorder.isPending}
                        aria-label={`Drag size ${v.option1 ?? index + 1} to reorder`}
                        title="Drag to reorder"
                        onDragStart={(event) => {
                          setDraggedVariantId(v.shopify_variant_id);
                          event.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={() => setDraggedVariantId(null)}
                        className="cursor-grab rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing disabled:cursor-default"
                      >
                        {reorder.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <GripVertical className="h-3.5 w-3.5" />
                        )}
                      </button>
                      <div className="flex flex-col">
                        <button
                          type="button"
                          disabled={index === 0 || reorder.isPending}
                          aria-label={`Move size ${v.option1 ?? index + 1} up`}
                          onClick={() => moveVariant(index, index - 1)}
                          className="rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-20"
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          disabled={index === orderedVariants.length - 1 || reorder.isPending}
                          aria-label={`Move size ${v.option1 ?? index + 1} down`}
                          onClick={() => moveVariant(index, index + 1)}
                          className="rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-20"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <VariantFieldCell
                      shopifyVariantId={v.shopify_variant_id}
                      shopifyProductId={v.shopify_product_id}
                      field="option1"
                      value={v.option1}
                      className="min-w-0"
                    />
                    {(v.option2 || v.option3) && (
                      <p className="truncate px-1.5 text-[10px] text-muted-foreground">
                        {[v.option2, v.option3].filter(Boolean).join(' / ')}
                      </p>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-2.5 py-1">
                <VariantFieldCell
                  shopifyVariantId={v.shopify_variant_id}
                  shopifyProductId={v.shopify_product_id}
                  field="sku"
                  value={v.sku}
                />
              </td>
              <td className="px-2.5 py-1">
                <VariantFieldCell
                  shopifyVariantId={v.shopify_variant_id}
                  shopifyProductId={v.shopify_product_id}
                  field="price"
                  value={v.online_price}
                />
              </td>
              <td className="px-2.5 py-1">
                <NurseryPriceEditor
                  shopifyVariantId={v.shopify_variant_id}
                  value={v.nursery_price}
                />
              </td>
              <td className="px-2.5 py-1">
                <VariantInventoryCell
                  shopifyVariantId={v.shopify_variant_id}
                  shopifyInventoryItemId={v.shopify_inventory_item_id}
                  value={v.inventory_quantity}
                />
              </td>
              <td className="px-2.5 py-1">
                <NurseryOnlyToggle
                  shopifyVariantId={v.shopify_variant_id}
                  value={v.nursery_only ?? false}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
